// The firmware fleet — every installed device, what it is running, and whether
// it needs updating.
//
// firmwareUpdate.js answers "will this device receive the command".
// firmwareInfo.js answers "is there any point sending it", for a handful.
// THIS answers "which devices across the whole estate need it", which is a
// different problem because of the call count.
//
// ── Where every column comes from, and why it costs what it costs ──────────
//
// Probed against the live server 2026-08-03. There is no bulk join for any of
// this, so the shape below is not a design preference — it is the only path:
//
//   GetAllLocations                 1 call    -> 117 locations
//   GetAllDevicesByLocationID       1/location -> UniqueNumber, RoomName,
//                                   DeviceName, DeviceTypeName, Connected,
//                                   NetworkLastUpdatedDateTime
//   GetDeviceDetailbyUniqueNumber   1/DEVICE  -> OTABinFileId, BinFileName,
//                                   HexFileDateTime, CodeUpdated
//
// ⚠️ THE LAST ONE IS THE EXPENSIVE ONE AND IT CANNOT BE AVOIDED. Three cheaper
// routes were probed and all three are dead:
//
//   • `GetAllOTABinFile` returns 15 rows of `{OTABinFileId, BinFileName}` and
//     NO date.
//
//     ⚠️ THE COLUMN EXISTS; THE DEPLOYED QUERY JUST DOES NOT SELECT IT. The
//     OTABinFile table carries HexFileName, HexFileUrl, HexFileDateTime and
//     UartPins per row — confirmed against the table itself, e.g. id 9 is
//     `OtaForTuya.ino.bin`, `HexFileDateTime 2026-08-02 12:19:25`. So this is a
//     three-word change to the backend's SELECT, not missing data.
//
//     ⚠️ AND IT WOULD NOT SAVE A SINGLE REQUEST. Widening it would let the app
//     show every known build and its date with no devices loaded, which is worth
//     having — but the per-device call stays either way, because `CodeUpdated`
//     (when THIS device last flashed) and the device's own `OTABinFileId` are
//     both per-device and neither has a bulk source. The comparison needs both
//     halves; the file's date is only one of them.
//   • `DeviceType.OtaUpdateDateTime` looks like the same date and is not safe
//     to use: the DEVICE's own OTABinFileId overrides its type's, and they
//     really do differ (935809 is a type-49 device carrying OTABinFileId 9).
//     Resolving by type names the wrong file for exactly the devices somebody
//     deliberately special-cased. See firmwareInfo.js.
//   • `GetallDeviceStatusForTuyaTesting` carries CodeUpdated in bulk, but it is
//     TIME-WINDOWED — it returned 5 rows at 13:38 and 0 rows for every
//     LimitCount an hour later. It can only ever show recently-live devices,
//     which is the opposite of the list wanted here.
//
// ── Liveness is free, and deliberately so ─────────────────────────────────
// `Connected` and `NetworkLastUpdatedDateTime` ride along on the per-location
// list, so "is it online" costs no extra request. An earlier design called
// getStatusCached per device on top of the detail call — two requests each,
// 2600 for the estate — for an answer already sitting in a reply we had.

import { getAllLocations, getDevicesByLocation, getDeviceDetail } from '../api/api';
import { firmwareStateOf, parseServerTime, CURRENT, STALE, UNKNOWN_STATE } from './firmwareInfo';
import { ONLINE_WINDOW_MS } from './deviceStatus';

export { CURRENT, STALE, UNKNOWN_STATE };

/**
 * Requests in flight at once.
 *
 * ⚠️ SIX, NOT MORE, and this is not caution for its own sake. A browser allows
 * about six connections per host; fire fifty and the ones at the back sit in a
 * queue until their own timeout fires — which reads as a failed lookup, and
 * gets worse the larger the scope. The same ceiling firmwareInfo and
 * firmwareUpdate already use, for the same reason.
 */
export const FLEET_CONCURRENCY = 6;

/** Promise.all with a ceiling. Results keep the order of `items`. */
async function mapLimit(items, limit, fn) {
  const out = new Array(items.length);
  let next = 0;
  const worker = async () => {
    while (next < items.length) {
      const i = next;
      next += 1;
      // eslint-disable-next-line no-await-in-loop
      out[i] = await fn(items[i], i);
    }
  };
  await Promise.all(Array.from({ length: Math.min(limit, items.length) }, () => worker()));
  return out;
}

export const FLEET_LIVE = 'live';
export const FLEET_OFFLINE = 'offline';

/**
 * Is this device currently there?
 *
 * ⚠️ THROUGH parseServerTime, ALWAYS. The backend writes IST wall-clock and the
 * JSON serialiser appends a `Z`, so a raw Date.parse reads every stamp 5½ hours
 * in the FUTURE — every device then looks freshly seen and NOTHING is ever
 * offline. See deviceStatus.SERVER_TZ_OFFSET_MS.
 *
 * `Connected` is the server's own flag and is trusted when it says 0; when it
 * says 1 the timestamp still has to agree, because the flag is written on
 * connect and is not cleared by a device that simply vanishes.
 */
// ⚠️ `liveState`, NOT `state`. A row carries BOTH a liveness answer and a
// firmware answer, and they are independent — an offline device can still be
// known to need an update. Naming both `state` had the firmware value quietly
// overwrite the liveness one on the merge, which showed every device as online.
export function livenessOf(row, now = Date.now()) {
  const at = parseServerTime(row?.NetworkLastUpdatedDateTime);
  if (at == null) return { liveState: FLEET_OFFLINE, lastSeen: null, age: null };
  const age = now - at;
  const live = Number(row?.Connected) !== 0 && age <= ONLINE_WINDOW_MS;
  return { liveState: live ? FLEET_LIVE : FLEET_OFFLINE, lastSeen: at, age };
}

/**
 * Every device at every location, with its location and room stamped on.
 *
 * @param onProgress ({ done, total, label }) => void
 * @param shouldStop () => boolean — checked between locations so a user leaving
 *        the screen does not leave 117 requests running behind them.
 */
export async function collectEstateDevices(onProgress, shouldStop) {
  const stop = () => (shouldStop ? shouldStop() : false);
  const say = (done, total, label) => { if (onProgress) onProgress({ done, total, label }); };

  say(0, 1, 'Loading locations…');
  const locRes = await getAllLocations();
  const locations = Array.isArray(locRes.data) ? locRes.data : [];
  if (!locations.length) return [];

  let done = 0;
  const perLocation = await mapLimit(locations, FLEET_CONCURRENCY, async (loc) => {
    if (stop()) return [];
    // ⚠️ LocationWithName is already "<owner>-<name>" and LocationName is not,
    // so the fuller one is preferred — on an estate list, two sites called
    // "Home" are indistinguishable without the owner.
    const locationName = loc.LocationWithName || loc.LocationName || `Location ${loc.LocationID}`;
    // Swallowed: one unreachable location must not empty the whole list. It
    // shows as a location with no devices, which is visibly different from an
    // error that took the page down.
    const res = await getDevicesByLocation(loc.LocationID).catch(() => null);
    const rows = Array.isArray(res?.data) ? res.data : [];
    done += 1;
    say(done, locations.length, `Reading ${locationName}…`);
    return rows.map((d) => ({
      uno: String(d.UniqueNumber),
      deviceName: d.DeviceName || `Smartify_${d.UniqueNumber}`,
      deviceTypeName: d.DeviceTypeName || '',
      // RoomName is null for a device that belongs to the location rather than
      // to a room — the gates, meters and locks. Named rather than left blank,
      // because a blank cell reads as missing data.
      roomName: String(d.RoomName || '').trim() || 'Common',
      locationId: loc.LocationID,
      locationName,
      raw: d,
    }));
  });

  // Deduped: a device mapped at two locations would otherwise be listed twice
  // and could be sent the command twice in one run.
  const seen = new Set();
  return perLocation.flat().filter((d) => {
    if (!d.uno || seen.has(d.uno)) return false;
    seen.add(d.uno);
    return true;
  });
}

/**
 * Firmware facts for a device list — one detail call each.
 *
 * A device whose lookup fails keeps state UNKNOWN rather than being dropped.
 *
 * ⚠️ UNKNOWN MEANS "NEEDS IT", not "skip it". `CodeUpdated` is stamped BEFORE
 * the flash runs (OtaForSwitching.ino:2097), so it records an ATTEMPT, not a
 * success — a device whose update failed looks current, and that is precisely
 * the device most worth retrying. Erring towards sending costs a redundant
 * reflash; erring the other way leaves broken firmware in place and reports
 * success.
 */
export async function loadFirmwareFacts(devices, onProgress, shouldStop) {
  const list = devices || [];
  const stop = () => (shouldStop ? shouldStop() : false);
  let done = 0;
  const now = Date.now();

  const out = await mapLimit(list, FLEET_CONCURRENCY, async (d) => {
    // Stopped mid-run: keep the row with what is already known rather than
    // dropping it, so a cancelled load shows a partial list instead of a gap.
    if (stop()) return { ...d, ...livenessOf(d.raw, now), state: UNKNOWN_STATE };
    const res = await getDeviceDetail(d.uno).catch(() => null);
    const row = Array.isArray(res?.data) ? res.data[0] : null;
    done += 1;
    if (onProgress) onProgress({ done, total: list.length, label: d.deviceName });
    const fw = row ? firmwareStateOf(row) : null;
    const live = livenessOf(d.raw, now);
    return {
      ...d,
      ...live,
      deviceId: row?.DeviceID ?? null,
      binFile: fw?.binFile || '',
      binUrl: fw?.binUrl || '',
      builtAt: fw?.builtAt ?? null,      // the OTA file's date
      flashedAt: fw?.flashedAt ?? null,  // the device's code date
      withinTolerance: !!fw?.withinTolerance,
      state: fw?.state || UNKNOWN_STATE,
    };
  });
  return out;
}

/** Counts for the summary chips. Offline is a SEPARATE axis from firmware. */
export function fleetCounts(rows) {
  const out = {
    total: 0, live: 0, offline: 0, needsUpdate: 0, upToDate: 0, unknown: 0,
  };
  (rows || []).forEach((r) => {
    out.total += 1;
    if (r.liveState === FLEET_LIVE) out.live += 1; else out.offline += 1;
    if (r.state === CURRENT) out.upToDate += 1;
    else if (r.state === STALE) out.needsUpdate += 1;
    else out.unknown += 1;
  });
  return out;
}
